import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Plug,
  Eye,
  PencilLine,
  Send,
  Power,
  Copy,
  Check,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";

const CONNECTOR_URL = "https://api.yourfreelanceros.com/mcp";

// Scope rows. `read`/`write`/`act` map 1:1 to the backend connector_settings doc.
const SCOPES = [
  {
    key: "read",
    icon: Eye,
    label: "Read",
    desc: "Let Claude see your clients, projects, invoices, and proposals.",
  },
  {
    key: "write",
    icon: PencilLine,
    label: "Write",
    desc: "Let Claude create and edit clients, leads, projects, and draft invoices/proposals.",
  },
  {
    key: "act",
    icon: Send,
    label: "Act",
    desc: "Let Claude send invoices to your clients.",
    warn: "Acting triggers real emails and Stripe payment links. Off by default. Every send still requires a second explicit confirmation.",
  },
];

export default function ConnectorSettingsPage() {
  const [settings, setSettings] = useState(null); // null until first load
  const [loadError, setLoadError] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get("/connector/settings");
        if (active) setSettings(res.data);
      } catch (e) {
        if (active) setLoadError(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // No optimistic flip: the Switch reflects server state, and only updates
  // after the PUT succeeds — so the UI can never claim a scope is on that
  // enforcement would still reject.
  const save = async (key, value) => {
    setSavingKey(key);
    try {
      const res = await api.put("/connector/settings", { [key]: value });
      setSettings(res.data);
      toast.success("Connector settings saved");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        (detail && (detail.message || detail)) || "Couldn't save — please try again"
      );
    } finally {
      setSavingKey(null);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(CONNECTOR_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the URL manually");
    }
  };

  const Header = (
    <div>
      <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
        <Plug className="w-6 h-6 text-indigo-400" />
        Claude Connector
      </h1>
      <p className="text-indigo-400">
        Control what Claude can do with your ClientKit data.
      </p>
    </div>
  );

  if (loadError) {
    return (
      <div className="space-y-6">
        {Header}
        <Alert className="bg-red-500/10 border-red-500/30">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <AlertDescription className="text-red-400">
            Couldn't load your connector settings. Refresh to try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="space-y-6">
        {Header}
        <div className="flex items-center gap-2 text-indigo-400 p-6">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      </div>
    );
  }

  const busy = savingKey !== null;

  return (
    <div className="space-y-6">
      {Header}

      {/* Master enable */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 flex items-center gap-2">
            <Power className="w-5 h-5 text-indigo-400" />
            Connector
          </CardTitle>
          <CardDescription className="text-indigo-400">
            The master switch. Turn this off to pause all Claude access — every tool
            stops responding until you turn it back on.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl border border-slate-700">
            <div>
              <h3 className="font-medium text-indigo-500">
                {settings.enabled ? "Enabled" : "Paused"}
              </h3>
              <p className="text-sm text-indigo-400">
                {settings.enabled
                  ? "Claude can use the scopes you allow below."
                  : "All connector access is paused."}
              </p>
            </div>
            <Switch
              checked={settings.enabled}
              disabled={busy}
              onCheckedChange={(v) => save("enabled", v)}
              aria-label="Enable connector"
            />
          </div>
        </CardContent>
      </Card>

      {/* Scopes */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500">Permissions</CardTitle>
          <CardDescription className="text-indigo-400">
            Choose what Claude is allowed to do. Changes take effect on Claude's next action.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {SCOPES.map(({ key, icon: Icon, label, desc, warn }) => {
            const disabled = busy || !settings.enabled;
            return (
              <div
                key={key}
                className={`p-4 bg-slate-900/50 rounded-xl border border-slate-700 ${
                  !settings.enabled ? "opacity-50" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <Icon className="w-5 h-5 text-indigo-400 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-indigo-500">{label}</h3>
                      <p className="text-sm text-indigo-400">{desc}</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings[key]}
                    disabled={disabled}
                    onCheckedChange={(v) => save(key, v)}
                    aria-label={`Allow ${label}`}
                  />
                </div>
                {warn && settings[key] && (
                  <Alert className="mt-3 bg-amber-500/10 border-amber-500/30">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <AlertDescription className="text-amber-300 text-sm">
                      {warn}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Connect instructions */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500">Connect Claude</CardTitle>
          <CardDescription className="text-indigo-400">
            Add this connector in Claude to manage your freelance business by chat.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-slate-900/70 rounded-lg border border-slate-700">
            <code className="text-sm text-emerald-300 flex-1 break-all font-mono">
              {CONNECTOR_URL}
            </code>
            <button
              type="button"
              onClick={copyUrl}
              className="shrink-0 inline-flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded-md hover:bg-slate-800 transition-colors"
              aria-label="Copy connector URL"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <ol className="list-decimal list-inside space-y-2 text-sm text-indigo-400">
            <li>In Claude (Desktop, Code, or claude.ai), open Settings → Connectors.</li>
            <li>Click <span className="text-indigo-300">Add custom connector</span>.</li>
            <li>Paste the URL above.</li>
            <li>Sign in with your ClientKit account when Claude prompts you.</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
